import React, { useContext } from "react";
import { View, StyleSheet } from "react-native";
import { PrimaryButton, TitleText, NormalText } from ".";
import ProgressBar from "../../uncommon/native/ProgressBar";
import { mixpanel } from "../../../utils/mixpanel";
import { SessionContext } from "../../../contexts";

const OnboardingWelcomeModal = ({ onNext }: { onNext: () => void }) => {
  const session = useContext(SessionContext)?.session;

  return (
    <View style={styles.container}>
      <ProgressBar progress={0} title="" timeLeft="" />

      <NormalText
        text="Welcome to ClinicOS 🚀"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "You’re minutes away from letting AI run the busywork while you focus on surgery and patient care.\n\n" +
          "• Automated follow‑ups that feel personal\n" +
          "• Consults prepped while you sleep\n" +
          "• Polished Before/Afters in one click\n" +
          "• A website widget that turns browsers into booked patients"
        }
        style={styles.body}
        numberOfLines={10}
      />
      <NormalText
        text="✨ ClinicOS is your clinic’s autopilot — and your time back."
        fontWeight="500"
        style={styles.emotionalHook}
        numberOfLines={2}
      />
      <PrimaryButton
        text="Next: Document Your Consults Automatically"
        // text="Next: Engage Patients with Simulations"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "welcome",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingDeployWidgetModal = ({ onNext }: { onNext: () => void }) => {
  const session = useContext(SessionContext)?.session;

  return (
    <View style={styles.container}>
      <ProgressBar progress={0.1} title="" timeLeft="" />

      <NormalText
        text="Deploy your Lead Conversion Widget"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "Your widget turns visitors into booked patients while you sleep. We'll walk through it together:\n\n" +
          "• Set the inbox that receives every new lead\n" +
          "• Preview and customize the widget on your site\n" +
          "• Tune the desktop and mobile experience\n" +
          "• Pick the right install guide for your website"
        }
        style={styles.body}
        numberOfLines={10}
      />
      <NormalText
        text="Let's get the widget live so leads start flowing."
        fontWeight="500"
        style={styles.emotionalHook}
        numberOfLines={2}
      />
      <PrimaryButton
        text="Set Up the Widget"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "deploy-widget-intro",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingDeployWidgetCompleteModal = ({
  onNext,
  nextActionLabel,
}: {
  onNext: () => void;
  nextActionLabel: string;
}) => {
  const session = useContext(SessionContext)?.session;
  return (
    <View style={styles.container}>
      <ProgressBar progress={0.2} title="" timeLeft="" />

      <NormalText
        text="Widget setup: ready to launch 🎉"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "Once the script is on your site, every visitor sees your widget — and every lead lands in your inbox automatically."
        }
        style={styles.body}
        numberOfLines={6}
      />
      <NormalText
        text="Now let's see what else ClinicOS does for you."
        fontWeight="500"
        style={styles.emotionalHook}
        numberOfLines={2}
      />
      <PrimaryButton
        text={nextActionLabel}
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "deploy-widget-complete",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingConsultModal = ({ onNext }: { onNext: () => void }) => {
  const session = useContext(SessionContext)?.session;

  return (
    <View style={styles.container}>
      <ProgressBar progress={0.14} title="" timeLeft="" />

      <NormalText
        text="Consults — Arrive Ready"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "Great consults start with great prep. With ClinicOS, you:\n\n" +
          "• Review your daily schedule at a glance\n" +
          "• Open an appointment and see everything: intake, notes, photos\n" +
          "• Record, transcribe, and summarize the consultation\n" +
          "• Show patients simulations of their potential results\n" +
          "• Browse your archive to show similar body types and cases"
        }
        style={styles.body}
        numberOfLines={10}
      />
      <NormalText
        text="Turn walk-ins into confident bookings — in minutes."
        fontWeight="500"
        style={styles.emotionalHook}
        numberOfLines={2}
      />
      <PrimaryButton
        text="Start a Consult"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "consult-intro",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingSimulationsModal = ({ onNext }: { onNext: () => void }) => {
  const session = useContext(SessionContext)?.session;
  return (
    <View style={styles.container}>
      <ProgressBar progress={0.28} title="" timeLeft="" />

      <NormalText
        text="Simulations — excite patients in seconds"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "Dr Palladino shows every visitor a personalised post‑op preview. It turns curiosity into consults and consults into booking.\n\n" +
          "How it helps you:\n" +
          "• Instant wow‑factor during consults\n" +
          "• Widget grabs lead info at peak intent\n" +
          "• Images auto‑save to EHR & CRM\n"
        }
        style={styles.body}
        numberOfLines={10}
      />
      <PrimaryButton
        text="Simulate a Procedure"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "simulations",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingSimulationsCompleteModal = ({
  onNext,
}: {
  onNext: () => void;
}) => {
  const session = useContext(SessionContext)?.session;
  return (
    <View style={styles.container}>
      <ProgressBar progress={0.42} title="" timeLeft="" />

      <NormalText
        text="Simulations: First success 🎉"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "You just delivered surgical excitement in under 5 seconds.\n\n" +
          "Your next patient will remember that feeling — and your close rate will show it."
        }
        style={styles.body}
        numberOfLines={6}
      />
      <NormalText
        text="Next up: Make your Before/Afters shine."
        style={styles.emotionalHook}
        fontWeight="500"
        numberOfLines={2}
      />
      <PrimaryButton
        text="Next: Create Stunning Before/Afters"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "simulations-complete",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingBeforeAfterModal = ({ onNext }: { onNext: () => void }) => {
  const session = useContext(SessionContext)?.session;
  return (
    <View style={styles.container}>
      <ProgressBar progress={0.57} title="" timeLeft="" />

      <NormalText
        text="Before / Afters — results that market themselves"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "After surgery, send every patient a branded Before/After. Reviews soar, referrals roll in, and patients see real progress during tough recovery days..\n\n" +
          "How it helps you:\n" +
          "• AI aligns, crops, and brands images for you\n" +
          "• Share anywhere with one tap\n" +
          "• Optional: EHR link for fully‑automated delivery"
        }
        style={styles.body}
        numberOfLines={10}
      />
      <NormalText
        text="Give every patient a share‑worthy trophy."
        style={styles.emotionalHook}
        numberOfLines={2}
        fontWeight="400"
      />
      <PrimaryButton
        text="Generate Before/Afters"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "before-after",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingBeforeAfterCompleteModal = ({
  onNext,
}: {
  onNext: () => void;
}) => {
  const session = useContext(SessionContext)?.session;
  return (
    <View style={styles.container}>
      <ProgressBar progress={0.71} title="" timeLeft="" />

      <NormalText
        text="Before / Afters: Done and delivered 🎉"
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "Gorgeous, branded images are ready to wow patients and visitors alike"
        }
        style={styles.body}
        numberOfLines={8}
      />
      <NormalText
        text="On to the next unlock…"
        fontWeight="500"
        style={styles.emotionalHook}
        numberOfLines={2}
      />
      <PrimaryButton
        text="Next: Future Growth Tools"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "before-after-complete",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const OnboardingFutureGrowthModal = ({ onNext }: { onNext: () => void }) => {
  const session = useContext(SessionContext)?.session;
  return (
    <View style={styles.container}>
      <ProgressBar progress={1} title="" timeLeft="" />

      <NormalText
        text="You're Just Getting Started."
        style={styles.header}
        fontWeight="600"
        numberOfLines={2}
      />
      <NormalText
        text={
          "Soon, you'll unlock even more capabilities:\n\n" +
          "• Automated lead nurturing & analytics\n" +
          "• Automatically manage and track all photos\n" +
          "• Track behavior and analytics with ease\n" +
          "• Let your clinic run itself — beautifully"
        }
        style={styles.body}
        numberOfLines={10}
      />
      <NormalText
        text="Let ClinicOS do the heavy lifting while you grow."
        style={styles.softCallout}
        numberOfLines={2}
        fontWeight="400"
      />
      <PrimaryButton
        text="Launch My Dashboard 🚀"
        onPress={() => {
          mixpanel.track("Onboarding Step Completed", {
            step: "future-growth",
            "User Email": session?.userEmail,
            clinic: session?.clinicName,
          });
          onNext();
        }}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "white",
    padding: 24,
    borderRadius: 16,
    maxWidth: 500,
    alignItems: "center",
  },
  header: {
    marginTop: 16,
    marginBottom: 16,
    textAlign: "center",
  },
  body: {
    marginBottom: 20,
    textAlign: "left",
  },
  emotionalHook: {
    fontStyle: "italic",
    textAlign: "center",
    marginBottom: 20,
    color: "#222",
  },
  softCallout: {
    fontStyle: "italic",
    textAlign: "center",
    marginBottom: 20,
    color: "#222",
  },
});

export {
  OnboardingWelcomeModal,
  OnboardingDeployWidgetModal,
  OnboardingDeployWidgetCompleteModal,
  OnboardingSimulationsModal,
  OnboardingSimulationsCompleteModal,
  OnboardingBeforeAfterModal,
  OnboardingBeforeAfterCompleteModal,
  OnboardingFutureGrowthModal,
  OnboardingConsultModal,
};
